import React from 'react';
import { useLongPress } from '../../hooks/useLongPress';

interface PressableBasketLineProps {
  /** Πάτημα (κλικ, Enter, Space). */
  onTap: () => void;
  /** Παρατεταμένο πάτημα ή δεξί κλικ· χωρίς αυτό (χωρίς δικαίωμα) το κράτημα δεν κάνει τίποτα. */
  onLongPress?: () => void;
  selected: boolean;
  /** Υπάρχουν επιλεγμένες γραμμές: το πάτημα επιλέγει/αποεπιλέγει (κουμπί εναλλαγής). */
  selectionMode: boolean;
  /** aria-label της γραμμής (π.χ. «Αλλαγή είδους: Μουσακάς»). */
  label: string;
  className?: string;
  testId?: string;
  children: React.ReactNode;
}

/**
 * Γραμμή του καλαθιού τραπεζιού (απόφαση ιδιοκτήτη 6/10/2026, όπως στο Take Away): πάτημα =
 * αλλαγή του είδους, παρατεταμένο πάτημα / δεξί κλικ = επιλογή για «Έκπτωση» / «Κέρασμα».
 * Οι κανόνες της χειρονομίας (κύλιση, ακύρωση, όχι διπλό) ζουν στο hooks/useLongPress·
 * εδώ μόνο η όψη: πατημένη γραμμή μικραίνει ελάχιστα και, όταν το κράτημα θα επιλέξει,
 * παίρνει απαλό περίγραμμα.
 */
const PressableBasketLine: React.FC<PressableBasketLineProps> = ({
  onTap, onLongPress, selected, selectionMode, label, className = '', testId, children
}) => {
  const { handlers, pressing } = useLongPress({ onTap, onLongPress });
  const tone = selected
    ? 'border-blue-500 ring-2 ring-blue-500/40'
    : pressing && onLongPress
      ? 'border-blue-400 ring-2 ring-blue-500/25'
      : 'border-line-strong';
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-pressed={selectionMode ? selected : undefined}
      data-testid={testId}
      data-pressing={pressing ? 'true' : undefined}
      {...handlers}
      style={{ WebkitTouchCallout: 'none' }}
      className={`bg-surface-2 rounded-xl border cursor-pointer select-none touch-manipulation transition-[transform,box-shadow,border-color] duration-150 ease-out motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${tone} ${pressing ? 'scale-[0.98]' : ''} ${className}`}
    >
      {children}
    </div>
  );
};

export default PressableBasketLine;
